import { mapGetters } from 'vuex';
import uniqBy from 'lodash/uniqBy';
import get from 'lodash/get';

import {
	RnModal,
	RnButton,
	RnField,
	RnIcon,
	RnForm,
	RnHeader,
	RnNewTable,
	RnCardList,
	RnDock,
	RnSidebar,
	RnStepForm,
	RnToggleButton,
	RnSpinner,
	RnTag,
	RnEditBatterySidebar,
} from '@/components';
import { MessageTypes } from '@/enums/message-types';
import { Roles } from '@/enums/roles';
import apiService from '@/services/api';
import { batteriesMixin } from '@/mixins';

import { ITEMS_PER_PAGE } from '@/services/constants';
import { logAndToastError } from '@/utils';
import RnPagination from '@/components/pagination/pagination.vue';

const DEFAULT_FILTER_BRAND_ID = '-';
const DEFAULT_FILTER_LOCATION_ID = '-';

export default {
	name: 'rn-cp-new-transport-view',
	components: {
		RnModal,
		RnPagination,
		RnButton,
		RnField,
		RnIcon,
		RnForm,
		RnHeader,
		RnNewTable,
		RnCardList,
		RnDock,
		RnSidebar,
		RnStepForm,
		RnToggleButton,
		RnSpinner,
		RnTag,
		RnEditBatterySidebar,
	},
	mixins: [batteriesMixin],
	data() {
		return {
			page: 0,
			ITEMS_PER_PAGE: ITEMS_PER_PAGE.DEFAULT,
			dock: {
				tableData: [],
				open: false,
				expanded: false,
			},
			sidebarOpen: false,
			editSidebarOpen: false,
			state: {
				currentStep: 1,
				currentEditBatteryStep: 1,
				selectedBatteries: [],
				date: null,
				location: DEFAULT_FILTER_LOCATION_ID,
				brand: DEFAULT_FILTER_BRAND_ID,
			},
		};
	},
	computed: {
		...mapGetters([
			'businessRoles',
			'location',
			'locations',
			'locationsByRoles',
			'checkedBatteries',
			'checkedBatteriesCount',
			'businessRelationId',
			'locationsFilterList',
			'brandsFilterList',
		]),
		steps() {
			const stepOneValid = !!this.state.selectedBatteries.length;
			const stepTwoValid = stepOneValid && !!this.state.destinationLocationId && !!this.state.date;

			return [
				{
					text: this.$t('stepForm.steps.selectBatteries'),
					form: this.editdBatteryForm,
					valid: () => {
						const battery = this.state.selectedBatteries[0];

						if (!battery) {
							this.$store.dispatch('setMessage', {
								text: 'cp.newTransport.batterySelection.warning',
								type: MessageTypes.WARNING,
							});

							return;
						}

						return stepOneValid;
					},
				},
				{
					text: this.$t('stepForm.steps.checkAndRequest'),
					valid: stepTwoValid,
					action: {
						icon: {
							name: 'plus',
							pos: 'right',
							width: '16px',
							height: '16px',
						},
						color: 'blue',
						method: this.assembleTransport,
						text: this.$t('common.updateBatteryLocation'),
					},
				},
			];
		},
		activeForm() {
			const activeIndex = Math.max(0, this.state.currentStep - 1);

			return this.steps[activeIndex].form || {};
		},
		batteriesLoadingState() {
			if (!this.checkedBatteries) {
				return 'loading';
			}
			if (this.checkedBatteries.length) {
				return 'results';
			}

			return 'noResults';
		},
		batteriesToSelect() {
			return (this.checkedBatteries || []).map(battery => {
				this.$set(
					battery,
					'selected',
					this.state.selectedBatteries.find(selectedBattery => selectedBattery.id === battery.id)
				);

				return battery;
			});
		},
		lastSelectedBattery() {
			return this.state.selectedBatteries[0] || null;
		},
		defaultBatteryTableHeaders() {
			return [this.brandBatteryTableHeader, this.locationBatteryTableHeader, this.formattedBatteryNumberHeader, ...this.existingCategorizedTableHeaders];
		},
		selectBatteryTableHeaders() {
			return [
				{
					text: '',
					value: 'selected',
					component: RnToggleButton,
					componentProps: value => {
						const isSelected = !!value.selected;

						return {
							active: isSelected,
							color: isSelected ? 'orange' : 'grey',
							icon: {
								name: isSelected ? 'check' : 'plus',
								width: '14px',
								height: '14px',
							},
							toggleColor: 'grey',
							toggleIcon: {
								name: 'cross',
								width: '14px',
								height: '14px',
							},
							method: () => {
								this.handleRowPress(value);
							},
						};
					},
				},
				...this.defaultBatteryTableHeaders,
			];
		},
		removeBatteryTableHeaders() {
			return [
				{
					text: '',
					value: 'selected',
					component: RnButton,
					componentProps: value => {
						return {
							icon: {
								name: 'cross',
								width: '14px',
								height: '14px',
							},
							color: 'grey',
							method: () => {
								this.removeBattery(value);
							},
							round: true,
						};
					},
				},
				...this.defaultBatteryTableHeaders,
			];
		},
		dockOpen() {
			const dockIsOpen = this.state.currentStep === 1 && this.dock.open && !!this.state.selectedBatteries.length;

			if (!dockIsOpen) {
				this.updateDock(false);
			}

			return dockIsOpen;
		},
		toggleDockLabel() {
			if (this.dock.expanded) {
				return this.$t('cp.newTransport.hideAllBatteries');
			}
			return this.$t('cp.newTransport.showAllBatteries');
		},
		destinationLocations() {
			return this.locationsByRoles.map(location => ({
				name: location.name,
				value: location.id,
			}));
		},
		selectableBrands() {
			return [
				{ name: this.$t('cp.newTransport.allBrands'), value: DEFAULT_FILTER_BRAND_ID },
				...this.brandsFilterList.map(brand => ({ name: brand.name, value: brand.id })),
			];
		},
		selectableLocations() {
			return [
				{ name: this.$t('cp.newTransport.allLocations'), value: DEFAULT_FILTER_LOCATION_ID },
				...this.locationsFilterList.map(location => ({ name: location.name, value: location.id })),
			];
		},
	},
	watch: {
		businessRoles: {
			immediate: true,
			handler(value) {
				if (value && value.length) {
					const roles = [Roles.RC, Roles.SD];
					const roleIds = this.$store.getters.businessRoleIds(roles);

					this.$store.dispatch('getLocationsByRoles', { roleIds, businessRelationId: this.businessRelationId });
				}
			},
		},
	},
	created() {
		this.updateBatteries(0);
		this.$store.dispatch('getLocationsFilterList', { businessRelationId: this.businessRelationId });
		this.$store.dispatch('getBrandsFilterList', { businessRelationId: this.businessRelationId });
	},
	methods: {
		handleRowPress(battery) {
			if (!battery) {
				return;
			}

			if (battery.selected) {
				this.removeBattery(battery.id);

				return;
			}

			this.addBattery(battery);
		},
		setFormValue(prop, value) {
			this.state[prop] = value;

			if (this.state.destinationError) {
				this.validateForm();
			}
		},
		validateForm() {
			this.state.destinationError = this.state.destination ? null : this.$t('error.required', [this.$t('common.destination')]);

			return this.state.destination;
		},
		getModelUpdater(model) {
			this.activeForm.modelEditHandler(model);
		},
		goBack() {
			this.$router.push({ name: 'Dashboard' });
		},
		updateBatteries(page) {
			this.page = page;
			this.getBatteries();
		},
		// filters (location & brand) are immediately set in the state, so they can be read from there.
		getBatteries() {
			this.$store.dispatch('getCheckedBatteries', {
				businessRelationId: this.businessRelationId,
				location: this.state.location === DEFAULT_FILTER_LOCATION_ID ? null : this.state.location,
				brand: this.state.brand === DEFAULT_FILTER_BRAND_ID ? null : this.state.brand,
				page: this.page,
				pageSize: ITEMS_PER_PAGE.DEFAULT,
			});
		},
		addBattery(battery) {
			if (this.state.selectedBatteries.length && battery.location.id !== this.state.selectedBatteries[0].location.id) {
				logAndToastError(this.$store.dispatch, this.$t('cp.newTransport.onlyOneLocation'));

				return;
			}

			if (!battery.selected) {
				battery.selected = true;

				this.state.selectedBatteries.push(battery);
				this.dock.open = true;

				if (!this.dock.expanded) {
					this.dock.tableData = [this.lastSelectedBattery];
				}
			}
		},
		removeBattery(batteryToRemove) {
			const batteryIndex = this.state.selectedBatteries.findIndex(battery => battery.id === batteryToRemove.id);

			this.state.selectedBatteries.splice(batteryIndex, 1);
			(this.checkedBatteries || []).forEach(battery => {
				if (battery.id === batteryToRemove.id) {
					battery.selected = false;
				}
			});
			this.updateDock(this.dock.expanded);
		},
		async assembleTransport() {
			const plannedPickUpDate = this.state.date ? this.state.date : null;
			const batteryIds = this.state.selectedBatteries.map(battery => battery.id);
			const destinationLocationId = this.state.destinationLocationId;

			if (!destinationLocationId) {
				logAndToastError(this.$store.dispatch, this.$t('error.required', [this.$t('cp.newCollection.destination.placeholder')]));

				throw new Error(this.$t('error.required', [this.$t('cp.newCollection.destination.placeholder')]));
			}

			if (!plannedPickUpDate) {
				logAndToastError(this.$store.dispatch, this.$t('error.required', [this.$t('cp.newCollection.date')]));

				throw new Error(this.$t('error.required', [this.$t('cp.newCollection.destination.placeholder')]));
			}

			let url;
			let body;

			try {
				const batteryLocationId = get(this.state, 'selectedBatteries[0].location.id');
				if (!batteryLocationId) {
					return logAndToastError(this.$store.dispatch, this.$t('cp.newTransport.noLocationIdFound'), null, {
						selectedBatteries: this.state.selectedBatteries,
					});
				}

				url = `/locations/${batteryLocationId}/collectionorders`;
				body = {
					batteryIds,
					destinationLocationId,
					plannedPickUpDate,
				};

				const collectionOrder = await apiService.post(url, body);

				if (!collectionOrder?.data?.id) {
					throw new Error('no collection order id found');
				}

				url = `/businessrelations/${this.businessRelationId}/collectionorders/${collectionOrder.data.id}`;
				body = {
					batteryIds,
					destinationLocationId,
					plannedPickUpDate,
					pickUpDate: plannedPickUpDate,
				};

				const response = await apiService.patch(url, body);

				if (response.data.success) {
					this.$store.dispatch('setMessage', {
						text: 'cp.newBatteryLocation.success',
						type: MessageTypes.SUCCESS,
					});
					this.$router.push({ name: 'Dashboard' });
				}
			} catch (err) {
				logAndToastError(this.$store.dispatch, this.$t('cp.newBatteryLocation.failed'), err, {
					url,
					body,
				});
				throw err;
			}
		},
		updateDock(expanded) {
			this.dock.expanded = expanded;

			if (this.state.selectedBatteries.length) {
				this.dock.tableData = expanded ? this.state.selectedBatteries : [this.lastSelectedBattery];
			}
		},
	},
};
